import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtMYTDate } from "@/lib/myt";

/** Dedicated Indicator Requests page — the exact existing IB request review
 *  functionality, moved from the Overview into its own sidebar destination. */
export default function IBIndicatorRequests() {
  const [requests, setRequests] = useState(null);
  const [decision, setDecision] = useState(null); // {id, action, remark}
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState("");

  const load = () => { api.get("/ib/indicator-requests").then((res) => setRequests(res.data)).catch(() => setRequests([])); };
  useEffect(() => { load(); }, []);

  const q = query.trim().toLowerCase();
  const filtered = requests === null ? null : !q ? requests : requests.filter((r) =>
    [r.member_uid, r.member_name, r.email, r.indicator_name].some((v) => (v || "").toLowerCase().includes(q))
  );

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/ib/indicator-requests/${decision.id}/decision`, {
        action: decision.action,
        remark: decision.remark,
      });
      toast.success(`Request ${decision.action === "approve" ? "approved — forwarded to admin" : "rejected"}`);
      setDecision(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="ib-indicator-requests-page">
      <div>
        <p className="eyebrow-label">Requests</p>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white mt-2">Indicator Requests</h1>
      </div>
      <div className="card-lux overflow-hidden" data-testid="ib-indicator-requests-section">
        <div className="px-6 pt-6">
          <p className="eyebrow-label">Indicator Requests</p>
          <p className="mt-1 text-xs text-[#64748B]">Indicator requests from members registered under you. First claims are approved directly by administration (read-only here); second-indicator requests await your review.</p>
          <div className="relative sm:max-w-sm mt-4">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search client by UID, username, name or email…"
              className="input-lux w-full text-sm pr-9"
              data-testid="ib-indicator-search-input"
            />
            {query && (
              <button onClick={() => setQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-white transition-colors" data-testid="ib-indicator-search-clear" aria-label="Clear search">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
        <div className="overflow-x-auto mt-4">
          <table className="w-full text-sm" data-testid="ib-indicator-requests-table">
            <thead>
              <tr className="border-b border-[#222530] text-left">
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide">Member</th>
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide">Indicator</th>
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide hidden sm:table-cell">Requested</th>
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide">Status</th>
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#222530]">
              {filtered === null ? (
                <tr><td colSpan={5} className="px-6 py-10 text-center text-[#64748B]">Loading requests...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={5} className="px-6 py-10 text-center text-[#64748B]" data-testid="ib-indicator-requests-empty">{q ? "No requests match your search." : "No indicator requests from your members."}</td></tr>
              ) : (
                filtered.map((r) => (
                  <tr key={r.id} className="hover:bg-[#0B0C10]/60 transition-colors" data-testid={`ib-request-row-${r.id.slice(-6)}`}>
                    <td className="px-6 py-4">
                      <p className="text-white font-medium">{r.member_name}</p>
                      <p className="text-xs text-[#64748B] font-mono">{r.member_uid}</p>
                      <p className="text-xs text-[#94A3B8] break-all" data-testid={`ib-request-email-${r.id.slice(-6)}`}>{r.email || "—"}</p>
                      {r.type === "first" && (
                        <span className="inline-block mt-1.5 text-[9px] font-mono tracking-widest uppercase rounded-full px-2 py-0.5 border text-[#94A3B8] bg-[#94A3B8]/10 border-[#94A3B8]/25" data-testid={`ib-request-first-claim-${r.id.slice(-6)}`}>
                          First Claim • Admin Approval
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-white">{r.indicator_name}</td>
                    <td className="px-6 py-4 text-[#94A3B8] hidden sm:table-cell">{r.created_at ? fmtMYTDate(r.created_at) : "—"}</td>
                    <td className="px-6 py-4">
                      <span className={`text-[10px] font-mono tracking-widest uppercase rounded-full px-2.5 py-1 border ${
                        r.status === "ib_review" ? "text-[#D4AF37] bg-[#D4AF37]/10 border-[#D4AF37]/30"
                        : r.status === "ib_approved" || r.status === "approved" ? "text-emerald-400 bg-emerald-400/10 border-emerald-400/30"
                        : r.status === "pending_admin" ? "text-[#94A3B8] bg-[#94A3B8]/10 border-[#94A3B8]/25"
                        : "text-red-400 bg-red-400/10 border-red-400/30"
                      }`} data-testid={`ib-request-status-${r.id.slice(-6)}`}>
                        {r.status === "ib_review" ? "Awaiting Your Review" : r.status === "ib_approved" ? "Approved — Admin Review" : r.status === "approved" ? "Active" : r.status === "pending_admin" ? "Admin Review" : "Rejected"}
                      </span>
                      {r.type === "first" && <p className="text-[10px] text-[#64748B] mt-1">First Claim — Approval handled instantly by Admin.</p>}
                      {r.ib_remark && <p className="text-[10px] text-[#64748B] mt-1">Your remark: {r.ib_remark}</p>}
                    </td>
                    <td className="px-6 py-4 text-right">
                      {r.status === "ib_review" && (
                        <div className="flex items-center justify-end gap-2 flex-wrap">
                          <button onClick={() => setDecision({ id: r.id, action: "approve", remark: "" })} className="text-xs text-emerald-400 border border-emerald-400/30 hover:bg-emerald-400/10 rounded-lg px-2.5 py-1.5 transition-colors" data-testid={`ib-request-approve-${r.id.slice(-6)}`}>
                            Approve
                          </button>
                          <button onClick={() => setDecision({ id: r.id, action: "reject", remark: "" })} className="text-xs text-red-400 border border-red-400/30 hover:bg-red-400/10 rounded-lg px-2.5 py-1.5 transition-colors" data-testid={`ib-request-reject-${r.id.slice(-6)}`}>
                            Reject
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {decision && (
          <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="ib-decision-modal">
            <div className="absolute inset-0 bg-black/70" onClick={() => setDecision(null)} />
            <div className="card-lux relative w-full max-w-md p-6 sm:p-8">
              <p className="eyebrow-label mb-2">{decision.action === "approve" ? "Approve Request" : "Reject Request"}</p>
              <h3 className="font-heading text-lg font-semibold text-white mb-6">Second Indicator Request</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">
                    Remark {decision.action === "reject" ? <span className="text-red-400">*</span> : <span className="text-[#64748B]">(optional)</span>}
                  </label>
                  <textarea className="input-lux min-h-[80px]" value={decision.remark} onChange={(e) => setDecision((d) => ({ ...d, remark: e.target.value }))} data-testid="ib-decision-remark-input" />
                </div>
                <div className="flex gap-3 pt-2">
                  <button onClick={submit} disabled={busy} className={`flex-1 py-2.5 text-sm rounded-lg font-medium transition-colors disabled:opacity-60 ${
                    decision.action === "approve" ? "btn-gold" : "bg-red-500/90 hover:bg-red-500 text-white"
                  }`} data-testid="ib-decision-confirm-button">
                    {busy ? "Submitting..." : decision.action === "approve" ? "Approve & Forward to Admin" : "Confirm Rejection"}
                  </button>
                  <button onClick={() => setDecision(null)} className="btn-outline-gold px-5 py-2.5 text-sm" data-testid="ib-decision-cancel-button">Cancel</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
